import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { TrendingUp } from 'lucide-react';

/**
 * HealthTrendChart — stacked area (SVG) of RAG counts across the last N weeks.
 * Data source: GET /api/master-projects/health/trend?weeks=N
 * No chart library — small enough to hand-roll.
 */
const SERIES = [
  { key: 'delayed',  label: 'Delayed',  var: 'var(--rag-red)' },
  { key: 'at_risk',  label: 'At risk',  var: 'var(--rag-amber)' },
  { key: 'on_track', label: 'On track', var: 'var(--rag-green)' },
  { key: 'on_hold',  label: 'On hold',  var: 'var(--neutral)' },
];

export function HealthTrendChart({ weeks = 8, testId = 'health-trend-chart' }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/master-projects/health/trend?weeks=${weeks}`)
      .then(r => setData(r.data))
      .catch(() => {});
  }, [weeks]);

  if (!data) return null;
  const points = data.weeks || [];

  if (points.length === 0) {
    return (
      <div className="card p-4 elev-1" data-testid={testId}>
        <div className="text-[11px] uppercase tracking-wider text-[hsl(var(--foreground-subtle))] font-semibold flex items-center gap-2">
          <TrendingUp size={12} />Weekly health trend
        </div>
        <div className="text-[12.5px] text-[hsl(var(--foreground-muted))] mt-2">
          Not enough history yet — this fills in as weekly snapshots accumulate.
        </div>
      </div>
    );
  }

  const w = 380;
  const h = 90;
  const padL = 18;
  const padR = 8;
  const padT = 6;
  const padB = 20;
  const innerW = w - padL - padR;
  const innerH = h - padT - padB;
  const maxTotal = Math.max(1, ...points.map(p => p.total || 1));
  const stepX = points.length > 1 ? innerW / (points.length - 1) : innerW;

  // For each week compute cumulative stacked y values per series (top-down).
  const stacks = points.map(p => {
    let acc = 0;
    const parts = {};
    for (const s of SERIES) {
      const v = p.counts?.[s.key] || 0;
      parts[s.key] = { from: acc, to: acc + v };
      acc += v;
    }
    return { p, parts, total: p.total || acc };
  });

  const yFor = (v) => padT + innerH - (v / maxTotal) * innerH;

  // Build stacked area paths per series.
  const paths = SERIES.map(s => {
    const top = stacks.map((st, i) => [padL + i * stepX, yFor(st.parts[s.key].to)]);
    const bot = stacks.map((st, i) => [padL + i * stepX, yFor(st.parts[s.key].from)]);
    const d = [
      `M ${top[0][0]} ${top[0][1]}`,
      ...top.slice(1).map(pt => `L ${pt[0]} ${pt[1]}`),
      ...bot.slice().reverse().map(pt => `L ${pt[0]} ${pt[1]}`),
      'Z',
    ].join(' ');
    return { key: s.key, d, color: `hsl(${s.var})` };
  });

  return (
    <div className="card p-4 elev-1" data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-wider text-[hsl(var(--foreground-subtle))] font-semibold flex items-center gap-2">
          <TrendingUp size={12} />Weekly health trend · last {points.length} week{points.length === 1 ? '' : 's'}
        </div>
        <div className="flex items-center gap-2 text-[10.5px]">
          {SERIES.map(s => (
            <span key={s.key} className="inline-flex items-center gap-1 text-[hsl(var(--foreground-muted))]">
              <span className="inline-block w-2 h-2 rounded-sm" style={{ background: `hsl(${s.var})` }} />
              {s.label}
            </span>
          ))}
        </div>
      </div>
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full mt-2" preserveAspectRatio="none">
        {/* Axis baseline */}
        <line x1={padL} x2={w - padR} y1={padT + innerH} y2={padT + innerH}
              stroke="hsl(var(--border))" strokeWidth={0.5} />
        {paths.map(p => (
          <path key={p.key} d={p.d} fill={p.color} fillOpacity="0.85" stroke={p.color} strokeWidth={0.75} />
        ))}
        {points.map((pt, i) => (
          <text key={i} x={padL + i * stepX} y={h - 6}
                textAnchor={i === 0 ? 'start' : i === points.length - 1 ? 'end' : 'middle'}
                fontSize={9} fill="hsl(var(--foreground-subtle))"
                fontFamily="var(--font-mono)">
            {pt.week_start?.slice(5) || pt.week}
          </text>
        ))}
      </svg>
    </div>
  );
}
